/**
 * Build a link that is meant to leave the app — a QR code, an invite, anything handed to
 * another person.
 *
 * WHY THIS EXISTS. These links were built from `window.location.origin`, which is correct
 * on web and wrong on every bundled native build. There the WebView origin is
 * `capacitor://localhost` (iOS) / `https://localhost` (Android), so the app produced:
 *
 *     capacitor://localhost/connect/<uuid>
 *
 * and put it in a QR code, on the clipboard, and into the system share sheet. A recipient
 * gets a link their phone cannot open. It went unnoticed because the one path that is
 * exercised in testing — scanning between two installs — still works: the scanner parses
 * the path and never looks at the scheme or host.
 *
 * The same class of bug as `nativeApiOrigin.ts` fixes for `/api/*` calls, and for the same
 * underlying reason: on native, the WebView origin is not the product's origin.
 *
 * WEB IS UNCHANGED and deliberately so. On web, whatever host the user is on is a real,
 * reachable host — including preview and staging deploys, where a link pointing at
 * production would be actively misleading while testing.
 */

import { isNative } from '@/utils/platform';
import { API_BASE_URL } from '@/lib/api';

/**
 * The public origin of the product, with no trailing slash.
 *
 * On native this is API_BASE_URL (VITE_API_URL) rather than a hard-coded constant: the
 * Fastify server serves the site and the API from the same host, so they cannot drift, and
 * a staging native build points at its own backend instead of silently at production.
 */
export function getPublicOrigin(): string {
  const origin = isNative ? API_BASE_URL : window.location.origin;
  return origin.replace(/\/$/, '');
}

/**
 * Absolute URL for `path` on the public origin.
 *
 * Use for anything a human other than the current user will open: QR codes, invite links,
 * share sheets, emails. Do NOT use it for in-app navigation — react-router paths stay
 * relative, and an absolute URL there would navigate the WebView off its own origin.
 */
export function shareableUrl(path: string): string {
  return `${getPublicOrigin()}${path.startsWith('/') ? path : `/${path}`}`;
}
